import React, { ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useQuery } from "@apollo/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faMap } from "@fortawesome/free-solid-svg-icons";
import { classNames, toGQLPeriod } from "@ct-react/core";
import { LocaleLink } from "@ct-react/locale";
import { AccommodationMarker, AccommodationType } from "@shared/models/article";
import { SearchAnyArticleEdge } from "../../models/search-param";
import { countGuests, SearchPagination } from "../../models/search";
import { main } from "./translations";
import { useScrollLock } from "../../contexts/lockScroll";
import { CmsContext } from "../../contexts/cms";
import { useSearchContext } from "../../contexts/search";
import { useDisplayDesktop } from "../../utils/breakpoints";
import {
  parsePaginationSearchParams,
  rebuildPaginationSearchParams,
  rebuildQuerySearchParams
} from "../../utils/search-params";
import { GET_ARTICLES } from "./search-articles-config";
import { CardArticle, CardArticleSkeleton } from "../../components/cards/card-articles";
import { MapAsyncPopupMarker, MapContainer } from "../../bundles/map-implement";
import SearchFilter from "../../components/search/search-filter";
import SearchSort from "../../components/search/search-sort";
import SearchNameFilter from "../../components/search/search-name-filter";
import SearchResultPagination from "../../components/search/search-result-pagination";
import "./search-articles.scss";

const normalizeArticlePrice = (article: any) => {
  if (!article) return article;

  const { seasonalPrice, annualPrice, salePrice, ...normalizedArticle } = article;

  return {
    ...normalizedArticle,
    price: seasonalPrice ?? annualPrice ?? salePrice
  };
};

const SearchArticles = ({ type }: { type: AccommodationType }) => {

  const intl = useIntl();
  const listSize = useRef<number>(12);

  const [ searchParams, setSearchParams ] = useSearchParams();
  const { navigation: cmsNavigation } = useContext(CmsContext);
  const { lockScroll, unlockScroll } = useScrollLock();

  const [ searchCriteria, changeCriteria ] = useSearchContext();
  const [ searchPagination, setSearchPagination ] = useState<SearchPagination>(parsePaginationSearchParams(searchParams));
  const [ searchSort, setSearchSort ] = useState<any | undefined>(undefined);
  const [ showMap, setShowMap ] = useState<boolean>(false);
  const [ cmsPage, setCmsPage ] = useState<{ path: string } | undefined>(undefined);

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const isSmartphone = !useDisplayDesktop();

  const titleIntl = useMemo(() => {
    switch (type) {
      case AccommodationType.RENTAL: return intl.formatMessage(main.search_articles_title_rental);
      case AccommodationType.SEASONAL: return intl.formatMessage(main.search_articles_title_seasonal);
      case AccommodationType.ANNUAL: return intl.formatMessage(main.search_articles_title_annual);
      case AccommodationType.SALE: return intl.formatMessage(main.search_articles_title_sale);
    }
  }, [ type, intl ]);

  const ctaIntl = useMemo(() => ({
    title: intl.formatMessage(type === AccommodationType.SALE ? main.cta_title_sale : main. cta_title_rent, {
      s: (chunks: ReactNode) => (<span>{chunks}</span>)
    }),
    lead: intl.formatMessage(type === AccommodationType.SALE ? main.cta_text_sale : main.cta_text_rent),
    button: intl.formatMessage(type === AccommodationType.SALE ? main.cta_button_sale : main.cta_button_rent)
  }), [ type, intl ])


  // statement changes

  // reset sorting and pagination on search page type changes
  useEffect(() => {
    !!searchSort && setSearchSort(undefined);
    setSearchPagination({ direction: "after" });
  }, [ type ]);

  // update search params on query changes
  useEffect(() => {
    rebuildQuerySearchParams(searchParams, searchCriteria[type]);
    rebuildPaginationSearchParams(searchParams, searchPagination);
    setSearchParams(searchParams, { replace: true });
  }, [ searchPagination, searchCriteria[type] ]);

  // match cms page for cta link
  useEffect(() => {
    const comparer = type === AccommodationType.SALE ? "e2d81526-cdb2-46b1-b18b-f8e7812f2230" : "55add8e6-4242-4c69-8874-c52c22db1bed";
    const search = cmsNavigation.services?.find(np => np.id === comparer);
    !!search && setCmsPage(search);
  }, [ cmsNavigation, type ]);

  const formattedQueryCriteria = useMemo(() => {
    switch (type) {
      case AccommodationType.RENTAL:
        return {
          rentalAccommodation: {
            ...searchCriteria[type],
            ...(!!searchCriteria[type].period) && { period: { period: toGQLPeriod(searchCriteria[type].period.period) } },
            ...(!!searchCriteria[type].guests) && { guests: countGuests(searchCriteria[type].guests) },
          }
        };
      case AccommodationType.SEASONAL: return { seasonalAccommodation: searchCriteria[type] };
      case AccommodationType.ANNUAL: return { annualAccommodation: searchCriteria[type] };
      case AccommodationType.SALE: return { saleAccommodation: searchCriteria[type] };
    }
  }, [ type, searchCriteria[type] ]);

  const formattedQuerySort = useMemo(() => {
    if (!searchSort) return undefined;
    switch (type) {
      case AccommodationType.RENTAL: return { rentalAccommodation: searchSort };
      case AccommodationType.SEASONAL: return { seasonalAccommodation: searchSort };
      case AccommodationType.ANNUAL: return { annualAccommodation: searchSort };
      case AccommodationType.SALE: return { saleAccommodation: searchSort };
    }
  }, [ searchSort ])

  const formattedQueryPagination = useMemo(() => ({
    ...(searchPagination.direction === "after") && {
      first: listSize.current,
      ...(!!searchPagination.cursor) && { after: searchPagination.cursor },
    },
    ...(searchPagination.direction === "before" && {
      last: listSize.current,
      ...(!!searchPagination.cursor) && { before: searchPagination.cursor },
    })
  }), [ searchPagination ]);

  // scroll to top on any search change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [ formattedQueryCriteria, formattedQueryPagination ]);

  // block scroll on mobile display map
  useEffect(() => {
    (showMap && isSmartphone) && lockScroll();
    (!showMap || !isSmartphone) && unlockScroll();
  }, [ showMap, isSmartphone ]);

  // data

  const { data, loading, error } = useQuery(GET_ARTICLES, {
    variables: {
      type: formattedQueryCriteria,
      ...(!!formattedQuerySort) && { sort: formattedQuerySort },
      ...formattedQueryPagination
    },
    ssr: true,
    fetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true
  });


  // dom interactions

  const handleShowMap = () => setShowMap(!showMap);

  const onSort = (value?: any) => {
    setSearchSort(value);
    setSearchPagination({ direction: "after" });
  }

  const onFilter = ({ type, criteria: payload }) => {
    setSearchPagination({ direction: "after" });
    changeCriteria({ type, payload });
  }


  // rendering

  const containerClasses = classNames("articles-container", { "with-map": showMap });
  const listClasses = classNames("articles", {
    "auto-grid": data?.search.edges.length > 3,
    "third-grid": data?.search.edges.length <= 3
  });

  return (
    <>

      <div className="articles-header">
        <h1>{intl.formatMessage(main.search_articles_title, { type: (<span>{titleIntl}</span>) })}</h1>
      </div>

      <div className="articles-settings">
        <SearchSort type={type}
                    data={searchSort}
                    onChange={onSort} />
        <SearchFilter type={type}
                      data={searchCriteria[type]}
                      onChange={onFilter} />
        <SearchNameFilter className="name-criteria"
                          type={type}
                          data={searchCriteria[type]}
                          onChange={onFilter} />
        {!isSmartphone &&
          <div className="toggle-map">
            {intl.formatMessage(main.search_articles_map)}
            <label className="switch">
              <input className="toggle-btn" type="checkbox" checked={showMap} onChange={handleShowMap} />
              <span className="slider round" />
            </label>
          </div>
        }
      </div>

      <div ref={containerRef}
           className={containerClasses}>

        {loading  &&
          <div className="articles">
            {Array.from({ length: 8 }).map((_, i) => (<CardArticleSkeleton key={i} />))}
          </div>
        }

        {(!loading && error) &&
          <div className="empty-articles">
            <h3>{intl.formatMessage(main.issue_occured)}</h3>
          </div>
        }

        {(!loading && !!data && data?.search.totalCount === 0) &&
          <div className="empty-articles">
            <h3>{intl.formatMessage(main.no_accomodations)}</h3>
            <p>{intl.formatMessage(main.modify_your_filters)}</p>
          </div>
        }

        {(!loading && !!data && data.search.edges.length > 0) &&
          <ul itemScope itemType="https://schema.org/OfferCatalog"
              className={listClasses}>
            {data.search.edges.map((article: SearchAnyArticleEdge, i: number) =>
              <li key={i}>
                <CardArticle type={type}
                             index={i + 1}
                             data={normalizeArticlePrice(article.node)}
                             bookingSuggestion={article.accommodationBookingSuggestion || undefined} />
              </li>
            )}
          </ul>
        }

        <div className="articles-map">
          <MapContainer ref={mapRef}
                        className="overloaded-map"
                        center={[ 46.31276174491367, 7.483991126029801 ]}
                        zoom={13}
                        clusterize={true}>
            {(!loading && !!data) && data.search.accommodationMapMarkers.map((marker: AccommodationMarker, i: number) =>
              <MapAsyncPopupMarker key={i} type={type} marker={marker} />
            )}
          </MapContainer>
        </div>

      </div>

      {isSmartphone &&
        <button className={classNames("btn-red", "mobile-map-swap", { "map-open": showMap })}
                onClick={handleShowMap}>
          <FontAwesomeIcon icon={showMap ? faChevronLeft : faMap} />
          {intl.formatMessage(showMap
            ? { id:"articles-see-list", defaultMessage: "Voir la liste" }
            : { id:"articles-see-map", defaultMessage: "Voir la carte" })}
        </button>
      }

      {(!loading && !!data) &&
        <SearchResultPagination data={data.search.paginationInfo} onChange={setSearchPagination} />
      }

      <section className="cta">
        <h2>{ctaIntl.title}</h2>
        <p>{ctaIntl.lead}</p>
        {!!cmsPage &&
          <LocaleLink className="btn-red" to={cmsPage.path}>{ctaIntl.button}</LocaleLink>
        }
      </section>
    </>);
}

export default SearchArticles;
